import { useState, useEffect } from 'react';

/**
 * Hook localStorage avec support multi-utilisateurs
 * @param key - Clé de base (ex: 'homeObjects')
 * @param initialValue - Valeur initiale si rien en localStorage
 * @param userPrefix - Préfixe optionnel (userId) pour éviter collision entre users
 */
function useLocalStorage<T>(key: string, initialValue: T, userPrefix?: string) {
  // Générer la clé finale (avec préfixe si user authentifié)
  const finalKey = userPrefix ? `${key}_${userPrefix}` : key;

  // État pour stocker notre valeur
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Obtenir de localStorage par clé (avec préfixe si fourni)
      const item = window.localStorage.getItem(finalKey);
      // Parser le JSON stocké ou si aucun, retourner initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // Si erreur, retourner initialValue
      console.log(error);
      return initialValue;
    }
  });

  // Recharger depuis localStorage quand le userPrefix change (login/logout)
  useEffect(() => {
    try {
      const item = window.localStorage.getItem(finalKey);
      if (item) {
        const parsedValue = JSON.parse(item);
        setStoredValue(parsedValue);
        console.log(`🔄 localStorage rechargé: ${finalKey}`, parsedValue);
      } else {
        // Pas de données pour ce user, utiliser initialValue
        setStoredValue(initialValue);
        console.log(`📝 localStorage initialisé: ${finalKey} avec valeur par défaut`);
      }
    } catch (error) {
      console.error(`❌ Erreur rechargement localStorage: ${finalKey}`, error);
      setStoredValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalKey]); // Se déclenche UNIQUEMENT quand finalKey change (= userPrefix change)

  // Retourner une version wrappée de useState's setter function qui persiste la nouvelle valeur dans localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Permettre à la valeur d'être une fonction donc on a la même API que useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Sauver l'état
      setStoredValue(valueToStore);
      // Sauver dans localStorage (avec préfixe si fourni)
      window.localStorage.setItem(finalKey, JSON.stringify(valueToStore));
    } catch (error) {
      // Une implémentation plus avancée gérerait l'erreur cas
      console.log(error);
    }
  };

  return [storedValue, setValue] as const;
}

export default useLocalStorage;